import {useCreateAccount} from '@/auth'
import {reportError} from '@/report-error'
import {useNavigate as useRemixNavigate} from '@remix-run/react'
import {commentRecordIdFromBlob, createComment, filesToIpfsBlobs} from '@seed-hypermedia/client'
import {
  HMBlockNode,
  HMMetadataPayload,
  HMPublishBlobsOutput,
  UnpackedHypermediaId,
} from '@seed-hypermedia/client/hm-types'
import {CommentEditor} from '@shm/editor/comment-editor'
import {
  getValidatedWebSeedLinkState,
  hmId,
  hypermediaUrlToRoute,
  idToUrl,
  queryAccount,
  queryDomain,
  queryKeys,
  routeToHref,
  routeToUrl,
  unpackHmId,
  useUniversalAppContext,
  useUniversalClient,
} from '@shm/shared'
import type {InlineEditCommentProps} from '@shm/shared/comments-service-provider'
import {NOTIFY_SERVICE_HOST} from '@shm/shared/constants'
import {useDocumentComments} from '@shm/shared/models/comments'
import {useAccount} from '@shm/shared/models/entity'
import {invalidateQueries, useQueryClient} from '@shm/shared/models/query-client'
import {applyOptimisticComment, buildOptimisticComment, navigateToComment} from '@shm/shared/optimistic-comment'
import {useTxString} from '@shm/shared/translation'
import {useNavigate, useNavRoute} from '@shm/shared/utils/navigation'
import {Button, buttonVariants} from '@shm/ui/button'
import {DialogTitle} from '@shm/ui/components/dialog'
import {EmailNotificationsSuccess} from '@shm/ui/email-notifications'
import {SizableText} from '@shm/ui/text'
import {Tooltip} from '@shm/ui/tooltip'
import {useAppDialog} from '@shm/ui/universal-dialog'
import {cn} from '@shm/ui/utils'
import {useMutation} from '@tanstack/react-query'
import {Check, SendHorizontal, X} from 'lucide-react'
import {ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useCommentDraftPersistence} from './comment-draft-utils'
import {EmailNotificationsForm} from './email-notifications'
import {hasPromptedEmailNotifications, setHasPromptedEmailNotifications, setPendingIntent} from './local-db'
import {processPendingIntent} from './pending-intent'
import {isPerfEnabled, markCommentSubmitEnd, markCommentSubmitStart, markEditorLoadEnd} from './web-perf-marks'

type PublishCommentInput = Awaited<ReturnType<typeof createComment>>

export type WebCommentingProps = {
  docId: UnpackedHypermediaId
  /** Comment ID from CommentEditorProps - used to resolve reply parent */
  commentId?: string | null
  isReplying?: boolean
  replyCommentVersion?: string | null
  replyCommentId?: string | null
  rootReplyCommentVersion?: string | null
  quotingBlockId?: string
  /** Codepoint range within the quoted block when commenting on a text fragment. */
  quotingRange?: {start: number; end: number}
  onSuccess?: (successData: {id: string; response: HMPublishBlobsOutput; commentPayload: PublishCommentInput}) => void
  /** Focus the editor on mount. */
  focusOnMount?: boolean
  /** @deprecated Use focusOnMount. Kept for older callers. */
  autoFocus?: boolean
}

export default function WebCommenting({
  docId,
  commentId,
  isReplying,
  replyCommentVersion: replyCommentVersionProp,
  rootReplyCommentVersion: rootReplyCommentVersionProp,
  replyCommentId: replyCommentIdProp,
  quotingBlockId,
  quotingRange,
  onSuccess,
  focusOnMount,
  autoFocus,
}: WebCommentingProps) {
  const quoting = useMemo(
    () => (quotingBlockId ? {blockId: quotingBlockId, range: quotingRange} : undefined),
    [quotingBlockId, quotingRange?.start, quotingRange?.end],
  )
  const tx = useTxString()
  const universalClient = useUniversalClient()
  const {getSigner, publish} = universalClient
  const {originHomeId} = useUniversalAppContext()

  // Resolve reply parent from commentId when explicit version props aren't provided
  const commentsService = useDocumentComments(docId)
  const resolvedReply = useMemo(() => {
    const id = replyCommentIdProp || commentId
    if (!id) return null
    const comment = commentsService.data?.comments?.find((c) => c.id === id)
    if (!comment) return null
    return {
      replyCommentId: comment.id,
      replyCommentVersion: comment.version,
      rootReplyCommentVersion: comment.threadRootVersion || comment.version,
    }
  }, [replyCommentIdProp, commentId, commentsService.data?.comments])

  const replyCommentId = replyCommentIdProp || resolvedReply?.replyCommentId
  const replyCommentVersion = replyCommentVersionProp || resolvedReply?.replyCommentVersion
  const rootReplyCommentVersion = rootReplyCommentVersionProp || resolvedReply?.rootReplyCommentVersion
  const isReplyEditor = isReplying || !!replyCommentId || !!commentId

  // Use draft persistence
  const {
    draft,
    draftMediaRefs,
    isLoading: isDraftLoading,
    saveDraft,
    removeDraft,
  } = useCommentDraftPersistence(docId.id, replyCommentId, quotingBlockId, quotingRange)

  // Track latest editor content for non-destructive reads (e.g. persisting intent to IDB)
  const latestBlocksRef = useRef<HMBlockNode[] | null>(null)

  // Generation counter: bumped on clearDraft to force editor remount via key
  const [editorGeneration, setEditorGeneration] = useState(0)
  const clearDraft = useCallback(() => {
    removeDraft()
    setEditorGeneration((g) => g + 1)
  }, [removeDraft])

  // Generate a stable draftId for IndexedDB media storage
  const draftId = useMemo(() => {
    const parts = ['comment-draft', docId.id]
    if (replyCommentId) parts.push(`reply-${replyCommentId}`)
    if (quotingBlockId) parts.push(`quote-${quotingBlockId}`)
    if (quotingRange) parts.push(`range-${quotingRange.start}-${quotingRange.end}`)
    return parts.join('-')
  }, [docId.id, replyCommentId, quotingBlockId, quotingRange?.start, quotingRange?.end])

  // Cleanup old draft media on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      import('./draft-media-db')
        .then(({cleanupOldDraftMedia}) => cleanupOldDraftMedia())
        .catch((err) => console.error('Failed to cleanup old draft media:', err))
    }
  }, [])

  const queryClient = useQueryClient()
  const route = useNavRoute()
  const navNavigate = useNavigate('replace')

  type PostCommentVars = {commentPayload: PublishCommentInput; contentBlocks: HMBlockNode[]}

  const postComment = useMutation({
    mutationFn: async ({commentPayload}: PostCommentVars) => {
      const commentBlobData = commentPayload.blobs[0]?.data
      if (!commentBlobData) throw new Error('No comment blob data')
      const commentId = await commentRecordIdFromBlob(commentBlobData)
      const response = await publish(commentPayload)
      return {response, commentId, commentPayload}
    },
    onMutate: async ({commentPayload, contentBlocks}: PostCommentVars) => {
      await queryClient.cancelQueries({queryKey: [queryKeys.DOCUMENT_COMMENTS, docId]})

      const authorMetadata: HMMetadataPayload | null = myAccount.data
        ? {id: myAccount.data.id, metadata: myAccount.data.metadata || null}
        : null

      const optimisticComment = await buildOptimisticComment({
        commentPayload,
        authorUid: userKeyPair!.id,
        docId,
        docVersion: docVersion!,
        contentBlocks,
        replyParentId: replyCommentId || undefined,
        threadRootVersion: rootReplyCommentVersion || undefined,
        quoting,
        visibility: 'PUBLIC',
      })

      applyOptimisticComment(queryClient, docId, optimisticComment, authorMetadata, quoting)
      navigateToComment(navNavigate, route, optimisticComment.id)
    },
    onError: (_err) => {
      // Keep the optimistic comment visible — the publish can be retried later.
      // Do NOT roll back cache or navigation; do NOT invalidate queries (would crash offline).
      console.warn('Comment publish failed, keeping optimistic comment:', _err)
      reportError(_err, {feature: 'web-comment', operation: 'publish', docId: docId.id})
    },
    onSuccess: ({response, commentId, commentPayload}) => {
      if (isPerfEnabled()) markCommentSubmitEnd()
      onSuccess?.({
        response,
        commentPayload: commentPayload,
        id: commentId,
      })
      invalidateQueries([queryKeys.DOCUMENT_ACTIVITY])
      invalidateQueries([queryKeys.DOCUMENT_DISCUSSION])
      invalidateQueries([queryKeys.DOCUMENT_COMMENTS])
      invalidateQueries([queryKeys.DOCUMENT_INTERACTION_SUMMARY])
      invalidateQueries([queryKeys.DOC_CITATIONS])
      invalidateQueries([queryKeys.BLOCK_DISCUSSIONS])
      invalidateQueries([queryKeys.ACTIVITY_FEED])
    },
  })

  const docVersion = docId.version
  const remixNavigate = useRemixNavigate()

  const {
    content: createAccountContent,
    userKeyPair,
    createAccount,
  } = useCreateAccount({
    onClose: () => {
      console.log('[commenting] onClose fired, calling processPendingIntent')
      processPendingIntent(originHomeId)
        .then((result) => {
          console.log('[commenting] processPendingIntent result:', result)
          if (result.type === 'comment') {
            clearDraft()
            remixNavigate(result.commentUrl)
          }
        })
        .catch((e) => {
          console.error('Failed to process pending intent after account creation:', e)
          reportError(e, {feature: 'web-comment', operation: 'process-pending-intent'})
        })
    },
  })

  const myAccount = useAccount(userKeyPair?.id || undefined)

  const {content: emailNotificationsPromptContent, open: openEmailNotificationsPrompt} =
    useAppDialog(EmailNotificationsPrompt)

  function promptEmailNotifications() {
    console.log('🔔 promptEmailNotifications called', {
      NOTIFY_SERVICE_HOST,
    })
    if (!NOTIFY_SERVICE_HOST) {
      console.log('❌ Email notifications disabled')
      return
    }
    hasPromptedEmailNotifications().then((hasPrompted) => {
      console.log('🔔 hasPrompted check:', hasPrompted)
      if (hasPrompted) {
        console.log('❌ User already prompted, skipping')
        return
      }
      console.log('✅ Opening email notifications prompt')
      openEmailNotificationsPrompt({})
    })
  }

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = useCallback(
    async (
      getContent: (
        prepareAttachments: (binaries: Uint8Array[]) => Promise<{
          blobs: {cid: string; data: Uint8Array}[]
          resultCIDs: string[]
        }>,
      ) => Promise<{
        blockNodes: HMBlockNode[]
        blobs: {cid: string; data: Uint8Array}[]
      }>,
      _reset: () => void,
    ) => {
      if (isSubmitting || !docVersion) return // Prevent double submission

      if (!userKeyPair) {
        // Persist intent to IDB so it can be processed after account creation
        // (works for both local and vault flows).
        // Use latestBlocksRef (updated on every editor change) instead of getContent(),
        // because getContent() destructively mutates editor blocks.
        try {
          const blocks = latestBlocksRef.current
          console.log('[commenting] saving intent, blocks:', blocks?.length ?? 'null')
          if (blocks) {
            await setPendingIntent({
              type: 'comment',
              docId,
              docVersion,
              content: blocks,
              replyCommentId: replyCommentId || undefined,
              replyCommentVersion: replyCommentVersion || undefined,
              rootReplyCommentVersion: rootReplyCommentVersion || undefined,
              quotingBlockId,
              quotingRange,
            })
            console.log('[commenting] intent saved to IDB')
          }
        } catch (e) {
          console.warn('Failed to persist pending comment intent:', e)
        }
        createAccount()
        return
      }

      try {
        setIsSubmitting(true)
        if (isPerfEnabled()) markCommentSubmitStart()
        if (!getSigner) throw new Error('getSigner not available')
        const signer = getSigner(userKeyPair.id)
        // Wrap getContent to capture block nodes before they're consumed by createComment
        let capturedBlocks: HMBlockNode[] = []
        const wrappedGetContent: typeof getContent = async (prepareAttachments) => {
          const result = await getContent(prepareAttachments)
          capturedBlocks = result.blockNodes
          return result
        }
        const commentPayload = await createComment(
          {
            getContent: wrappedGetContent,
            docId,
            docVersion,
            replyCommentVersion,
            rootReplyCommentVersion,
            quoting,
            prepareAttachments,
          },
          signer,
        )
        await postComment.mutateAsync({commentPayload, contentBlocks: capturedBlocks})
        console.log('✅ Comment posted successfully, calling promptEmailNotifications')
        clearDraft()
        // Clean up associated media from IndexedDB after successful publish
        if (typeof window !== 'undefined') {
          import('./draft-media-db')
            .then(({deleteAllDraftMediaForDraft, revokeHMBlockObjectURLs}) => {
              // Revoke object URLs before cleanup to free memory
              if (draft) revokeHMBlockObjectURLs(draft)
              return deleteAllDraftMediaForDraft(draftId)
            })
            .catch((err) => console.error('Failed to cleanup draft media:', err))
        }
        // TODO: bring back email notifications prompt for new notification system - only prompt if the user does not already notifs set up on the notify service.
        // promptEmailNotifications()
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      isSubmitting,
      userKeyPair,
      docId,
      docVersion,
      replyCommentVersion,
      rootReplyCommentVersion,
      quotingBlockId,
      quotingRange?.start,
      quotingRange?.end,
      quoting,
      createAccount,
      postComment,
      clearDraft,
      promptEmailNotifications,
    ],
  )
  const onAvatarPress = useMemo(() => {
    if (!userKeyPair) {
      return createAccount
    }
    return undefined
  }, [userKeyPair])

  const publishButtonEventClass = userKeyPair
    ? 'plausible-event-name=Publish+Comment'
    : 'plausible-event-name=start-create-account'

  // Re-inject mediaRefs into draft blocks so the editor can restore IndexedDB media
  const initialBlocks = useMemo(() => {
    if (!draft) return undefined
    if (!draftMediaRefs) return draft
    return draft.map((node) => {
      const block = node.block as any
      const blockId = block?.id
      if (!blockId || !draftMediaRefs[blockId]) return node
      return {
        ...node,
        block: {
          ...block,
          attributes: {
            ...block.attributes,
            mediaRef: JSON.parse(draftMediaRefs[blockId]),
          },
        },
      }
    }) as HMBlockNode[]
  }, [draft, draftMediaRefs])

  // Mark editor as loaded for performance measurement (only in perf test environment)
  // isPerfEnabled() is static at runtime, so this conditional hook is safe
  if (isPerfEnabled()) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      markEditorLoadEnd()
    }, [])
  }

  // Don't render until draft is loaded or doc version is missing
  if (isDraftLoading || !docVersion) {
    return !docVersion ? null : <div className="w-full">Loading…</div>
  }

  return (
    <div className="w-full">
      <CommentEditor
        key={`${draftId}-${editorGeneration}`}
        focusOnMount={focusOnMount ?? autoFocus}
        isReplying={isReplyEditor}
        handleSubmit={handleSubmit}
        initialBlocks={initialBlocks}
        onContentChange={(blocks, mediaRefs) => {
          latestBlocksRef.current = blocks
          saveDraft(blocks, mediaRefs)
        }}
        onAvatarPress={onAvatarPress}
        importWebFile={(url) => importWebFile(url, draftId)}
        handleFileAttachment={(file) => handleFileAttachment(file, draftId)}
        universalClient={universalClient}
        getDraftMediaBlob={async (draftId, mediaId) => {
          if (typeof window === 'undefined') return null
          try {
            const {getDraftMedia} = await import('./draft-media-db')
            const mediaData = await getDraftMedia(draftId, mediaId)
            if (!mediaData) {
              console.warn(`Media not found in IndexedDB: ${draftId}/${mediaId}`)
            }
            return mediaData?.blob || null
          } catch (error) {
            console.error('Failed to get draft media blob:', error)
            return null
          }
        }}
        submitButton={({getContent, reset}) => {
          return (
            <Tooltip
              content={tx(
                'publish_comment_as',
                ({name}: {name: string | undefined}) => (name ? `Publish Comment as ${name}` : 'Publish Comment'),
                {name: myAccount.data?.metadata?.name},
              )}
            >
              <button
                disabled={isSubmitting}
                className={cn(
                  buttonVariants({size: 'icon', variant: 'ghost'}),
                  publishButtonEventClass,
                  'flex items-center justify-center rounded-sm p-2 text-neutral-800 hover:bg-neutral-200 dark:text-neutral-200 dark:hover:bg-neutral-700',
                  isSubmitting && 'cursor-not-allowed opacity-50',
                )}
                onClick={() => handleSubmit(getContent, reset)}
              >
                <SendHorizontal className="size-4" />
              </button>
            </Tooltip>
          )
        }}
        account={myAccount.data}
        perspectiveAccountUid={myAccount.data?.id.uid} // TODO: figure out if this is the correct value
      />
      {createAccountContent}
      {emailNotificationsPromptContent}
    </div>
  )
}

async function prepareAttachments(binaries: Uint8Array[]) {
  return filesToIpfsBlobs(binaries)
}

// UUID v4 with safe fallback for browsers without crypto.randomUUID (Safari < 15.4)
function generateUUID(): string {
  // Native UUID
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  // Fallback to crypto.getRandomValues
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16)
    crypto.getRandomValues(bytes)

    // RFC4122 v4 bits
    bytes[6] = (bytes[6]! & 0x0f) | 0x40
    bytes[8] = (bytes[8]! & 0x3f) | 0x80

    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  }

  // Last resort fallback. Not cryptographically strong, but avoids hard failure
  return `fallback-${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`
}

async function handleFileAttachment(
  file: Blob,
  draftId?: string,
): Promise<{
  displaySrc: string
  fileBinary?: Uint8Array
  mediaRef?: {
    draftId: string
    mediaId: string
    name: string
    mime: string
    size: number
  }
}> {
  const fileBuffer = await file.arrayBuffer()
  const fileBinary = new Uint8Array(fileBuffer)

  // If draftId provided and we're in browser, use IndexedDB
  if (draftId && typeof window !== 'undefined') {
    try {
      const {putDraftMedia} = await import('./draft-media-db')
      const mediaId = generateUUID()
      const name = (file as File).name || `media-${Date.now()}`
      const mime = file.type || 'application/octet-stream'
      const size = file.size

      await putDraftMedia(draftId, mediaId, file, {name, mime, size})

      return {
        displaySrc: URL.createObjectURL(file),
        mediaRef: {
          draftId,
          mediaId,
          name,
          mime,
          size,
        },
      }
    } catch (error) {
      // Enhanced error logging to diagnose iOS Safari issues
      const errorName = error instanceof Error ? error.name : 'Unknown'
      const errorMsg = error instanceof Error ? error.message : String(error)

      console.warn(`IndexedDB storage failed (${errorName}), falling back to binary:`, errorMsg)

      // Log specific Safari issues
      if (errorName === 'QuotaExceededError') {
        console.warn('Storage quota exceeded. Consider clearing old drafts.')
      } else if (errorName === 'SecurityError') {
        console.warn('Private browsing or cross-origin restriction detected.')
      }

      // Fall through to legacy behavior
    }
  }

  // Legacy behavior: store as binary in the block
  return {
    displaySrc: URL.createObjectURL(file),
    fileBinary,
  }
}

async function importWebFile(
  url: string,
  draftId?: string,
): Promise<{
  displaySrc: string
  fileBinary?: Uint8Array
  type: string
  size: number
}> {
  try {
    const res = await fetch(url, {method: 'GET', mode: 'cors'})

    if (!res.ok) {
      throw new Error(`Failed to fetch: ${res.status} ${res.statusText}`)
    }

    const contentType = res.headers.get('content-type') || 'application/octet-stream'
    const blob = await res.blob()

    const result = await handleFileAttachment(blob, draftId)

    return {
      displaySrc: result.displaySrc,
      fileBinary: result.fileBinary,
      type: contentType,
      size: blob.size,
    }
  } catch (err: any) {
    throw new Error(err?.message || 'Could not download file.')
  }
}

export function useOpenUrlWeb() {
  const {originHomeId} = useUniversalAppContext()
  const client = useUniversalClient()
  const queryClient = useQueryClient()

  return async (url?: string, newWindow?: boolean) => {
    if (!url) return

    const unpacked = unpackHmId(url)
    const parsedRoute = hypermediaUrlToRoute(url) || (unpacked ? ({key: 'document', id: unpacked} as const) : null)
    const routeId =
      parsedRoute && typeof parsedRoute !== 'string' && 'id' in parsedRoute && typeof parsedRoute.id === 'object'
        ? parsedRoute.id
        : unpacked
    let newUrl = parsedRoute
      ? routeToHref(parsedRoute, {originHomeId})
      : unpacked
        ? idToUrl(unpacked, {originHomeId})
        : url

    if (!newUrl) {
      console.error('URL is empty', newUrl)
      return
    }

    if (routeId?.uid && parsedRoute) {
      const fallbackHref = routeToHref(parsedRoute, {originHomeId})
      const explicitOrigin =
        unpacked?.hostname && unpacked?.scheme
          ? `${unpacked.scheme}://${unpacked.hostname}`
          : routeId.hostname && routeId.scheme
            ? `${routeId.scheme}://${routeId.hostname}`
            : null
      const account =
        explicitOrigin || !routeId.uid ? null : await queryClient.fetchQuery(queryAccount(client, routeId.uid))
      const candidateOrigin = explicitOrigin || account?.metadata?.siteUrl || null
      const candidateHostname = candidateOrigin ? new URL(candidateOrigin).hostname : null
      const candidateUrl =
        candidateOrigin && parsedRoute
          ? routeToUrl(parsedRoute, {
              hostname: candidateOrigin,
              originHomeId: hmId(routeId.uid),
            })
          : newUrl
      const domainInfo = candidateHostname
        ? await queryClient.fetchQuery(queryDomain(client, candidateHostname, true))
        : null
      const linkState = getValidatedWebSeedLinkState({
        href: candidateUrl,
        fallbackHref,
        hostname: candidateHostname,
        expectedAccountUid: routeId.uid,
        registeredAccountUid: domainInfo?.registeredAccountUid,
        isSeedLink: true,
      })
      newUrl = linkState.href || newUrl
    }

    if (newWindow) {
      window.open(newUrl, '_blank')
    } else {
      window.location.assign(newUrl)
    }
  }
}

function EmailNotificationsPrompt({onClose}: {onClose: () => void}) {
  useEffect(() => {
    console.log('📧 EmailNotificationsPrompt mounted')
    setHasPromptedEmailNotifications(true)
  }, [])
  const [mode, setMode] = useState<'prompt' | 'form' | 'success'>('prompt')
  const [subscribedEmail, setSubscribedEmail] = useState<string | null>(null)

  if (mode === 'prompt') {
    return (
      <>
        <DialogTitle>Email Notifications</DialogTitle>
        <SizableText>
          Do you want to receive an email when someone mentions your or replies to your comments?
        </SizableText>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => onClose()}>
            No Thanks
          </Button>
          <Button variant="default" onClick={() => setMode('form')}>
            Yes, Notify Me
          </Button>
        </div>
      </>
    )
  }
  if (mode === 'form') {
    return (
      <>
        <DialogTitle>Email Notifications</DialogTitle>
        <EmailNotificationsForm
          onClose={onClose}
          onComplete={(email: string) => {
            setMode('success')
            setSubscribedEmail(email)
          }}
        />
      </>
    )
  }
  if (mode === 'success') {
    return (
      <>
        <DialogTitle>Subscription Complete!</DialogTitle>
        <EmailNotificationsSuccess email={subscribedEmail} onClose={onClose} />
      </>
    )
  }
  return null
}

/** Renders an inline editor for editing a comment on the web. */
export function renderWebInlineEditor({comment, onSave, onCancel, isSaving}: InlineEditCommentProps): ReactNode {
  return <WebInlineEditBox comment={comment} onSave={onSave} onCancel={onCancel} isSaving={isSaving} />
}

/** Inline comment editor for the web platform. */
export function WebInlineEditBox({comment, onSave, onCancel, isSaving}: InlineEditCommentProps) {
  const client = useUniversalClient()
  const handleSubmit = useCallback(
    async (
      getContent: (
        prepareAttachments: (binaries: Uint8Array[]) => Promise<{
          blobs: {cid: string; data: Uint8Array}[]
          resultCIDs: string[]
        }>,
      ) => Promise<{blockNodes: HMBlockNode[]; blobs: {cid: string; data: Uint8Array}[]}>,
      reset: () => void,
    ) => {
      const {blockNodes, blobs} = await getContent(prepareAttachments)
      if (blobs.length) {
        await client.publish({blobs: blobs.map((blob) => ({cid: blob.cid, data: blob.data}))})
      }
      onSave(blockNodes)
    },
    [client, onSave],
  )

  return (
    <div className="flex flex-col gap-2">
      <CommentEditor
        focusOnMount
        isReplying={false}
        handleSubmit={handleSubmit}
        initialBlocks={comment.content}
        importWebFile={(url) => importWebFile(url)}
        handleFileAttachment={(file) => handleFileAttachment(file)}
        universalClient={client}
        hideAvatar
        submitButton={({getContent, reset}) => (
          <>
            <Button variant="ghost" size="icon" onClick={onCancel} disabled={isSaving}>
              <X className="size-4" />
            </Button>
            <Tooltip content="Save edit">
              <Button
                size="icon"
                onClick={(e) => {
                  e.stopPropagation()
                  handleSubmit(getContent, reset)
                }}
                disabled={isSaving}
              >
                <Check className="size-4" />
              </Button>
            </Tooltip>
          </>
        )}
      />
    </div>
  )
}
